'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { Twitter, Instagram, ArrowUpRight, Mail } from 'lucide-react';
import { cn } from '../../lib/utils';
import { typography, gradients } from '../../styles/theme/variants';
import { useState, useEffect } from 'react';
import React from 'react';
import { StoreDownloadLink } from '../analytics/StoreDownloadLink';

const TikTokIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// Define icon types more explicitly
interface IconProps {
  className?: string;
}

type IconComponent = React.FC<IconProps>;

interface SocialLink {
  name: string;
  href: string;
  icon: React.ReactNode;
}

const footerLinks = {
  product: [
    { name: 'All Features', href: '/features' },
    { name: 'Tip Tracking', href: '/features/tip-tracker-for-servers-bartenders' },
    { name: 'Income Analytics', href: '/features/tip-income-analytics' },
    { name: 'Team Tip Sheets', href: '/features/tip-pooling-tip-sheets' },
    { name: 'Paychecks & Taxes', href: '/features/paycheck-wage-tax-tracking' },
    { name: 'ServerLife Import', href: '/features/serverlife-csv-import' },
    { name: 'Pricing', href: '/pricing' },
  ],
  resources: [
    { name: 'Blog', href: '/blog' },
    { name: 'Compare Apps', href: '/compare' },
    { name: 'Support', href: '/support' },
    { name: 'Affiliates', href: '/affiliates' },
  ],
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/contact' },
  ],
  social: [
    { name: 'Twitter', href: 'https://x.com/tipkeepr', icon: <Twitter className="h-5 w-5" /> },
    { name: 'Instagram', href: 'https://www.instagram.com/tipkeepr/?locale=en_US%2Cen_US', icon: <Instagram className="h-5 w-5" /> },
    { name: 'TikTok', href: 'https://www.tiktok.com/@tipkeepr?lang=en', icon: <TikTokIcon /> },
  ],
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

export function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | null }>({
    text: '',
    type: null,
  });

  // Auto-hide message after 5 seconds
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (message.text) {
      timeoutId = setTimeout(() => {
        setMessage({ text: '', type: null });
      }, 5000);
    }
    
    // Clean up timeout when component unmounts or message changes
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [message.text]);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset previous messages
    setMessage({ text: '', type: null });
    
    // Validate email
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage({ text: 'Please enter a valid email address', type: 'error' });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setMessage({ text: data.message || 'Successfully subscribed!', type: 'success' });
        setEmail(''); // Clear the input on success
      } else {
        setMessage({ 
          text: data.message || 'Failed to subscribe. Please try again.', 
          type: 'error' 
        });
      }
    } catch (error) {
      setMessage({ text: 'An error occurred. Please try again later.', type: 'error' });
      console.error('Newsletter subscription error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer role="contentinfo" className="relative overflow-hidden bg-white">
      {/* Premium Background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Base grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(to_right,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [transform:perspective(1000px)_rotateX(60deg)] opacity-20" />
        
        {/* Gradient orbs */}
        <motion.div
          className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-primary/3 to-primary-light/3 blur-[100px] opacity-50"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-primary-light/3 to-primary/3 blur-[100px] opacity-50"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      {/* Main Footer Content */}
      <div className="relative border-t border-black/5">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 py-20"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="space-y-12">
            {/* Brand Section */}
            <motion.div
              variants={itemVariants}
              className="text-center lg:text-left"
            >
              <div>
                <Link href="/" className="inline-flex items-center gap-3 group">
                  <span className="text-2xl font-bold bg-gradient-to-r from-black via-primary to-black bg-clip-text text-transparent bg-[length:200%_auto] animate-text-shimmer">
                    TipKeepr
                  </span>
                </Link>
                <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                  Mobile tip tracking for servers, bartenders, baristas, and other service professionals. Record tips and hours for free, then add deeper analytics and professional tools when you need them.
                </p>
              </div>

              {/* Newsletter Signup */}
              <div className="mt-8 space-y-4">
                <h2 className="text-sm font-semibold text-black">Stay Updated on New Features</h2>
                <form onSubmit={handleSubscribe} className="space-y-2">
                  <div className="flex gap-2 max-w-md mx-auto lg:mx-0">
                    <label htmlFor="footer-email" className="sr-only">
                      Email address for newsletter
                    </label>
                    <input
                      id="footer-email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="newsletter-field flex-1 rounded-xl"
                      disabled={isSubmitting}
                      aria-label="Email address for newsletter subscription"
                    />
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98, y: 0 }}
                      className={`newsletter-button rounded-xl ${
                        isSubmitting
                          ? 'bg-gray-400 cursor-not-allowed hover:scale-100 hover:translate-y-0'
                          : 'bg-black hover:bg-black/90'
                      }`}
                    >
                      {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                    </motion.button>
                  </div>
                  <div aria-live="polite" aria-atomic="true">
                    {message.text && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        role={message.type === 'error' ? 'alert' : 'status'}
                        className={`text-sm p-2 rounded max-w-md mx-auto lg:mx-0 ${
                          message.type === 'success' 
                            ? 'text-green-700 bg-green-50' 
                            : message.type === 'error'
                            ? 'text-red-700 bg-red-50'
                            : ''
                        }`}
                      >
                        {message.text}
                      </motion.div>
                    )}
                  </div>
                </form>
              </div>
            </motion.div>

            {/* Links Grid - Mobile Optimized */}
            <motion.div variants={itemVariants}>
              <div className="grid grid-cols-3 gap-x-4 gap-y-8 sm:grid-cols-4 lg:grid-cols-4 lg:gap-x-8">
                {/* Links Sections */}
                {Object.entries({
                  'Product': footerLinks.product,
                  'Resources': footerLinks.resources,
                  'Company': footerLinks.company,
                }).map(([title, links]) => (
                  <div key={title} className="space-y-3">
                    <h2 className="text-sm font-semibold text-black tracking-wide">{title}</h2>
                    <ul className="space-y-2.5">
                      {links.map((link) => (
                        <li key={link.name}>
                          <Link
                            href={link.href}
                            className="group inline-flex items-center text-gray-600 hover:text-primary transition-colors text-sm leading-relaxed py-1"
                          >
                            {link.name}
                            <ArrowUpRight className="w-3 h-3 ml-1 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {/* Download Section - Mobile Enhanced */}
                <div className="space-y-3">
                  <h2 className="text-sm font-semibold text-black tracking-wide">Download</h2>
                  <div className="flex flex-col gap-2.5">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <StoreDownloadLink platform="ios" placement="footer" className="block transition-all duration-200 touch-manipulation" style={{ minHeight: '44px' }}>
                        <img
                          src="/appstore.png"
                          alt="Download on the App Store"
                          className="h-9 w-auto rounded-lg"
                          loading="lazy"
                        />
                      </StoreDownloadLink>
                    </motion.div>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <StoreDownloadLink platform="android" placement="footer" className="block transition-all duration-200 touch-manipulation" style={{ minHeight: '44px' }}>
                        <img
                          src="/googleplay.png"
                          alt="Get it on Google Play"
                          className="h-9 w-auto rounded-lg"
                          loading="lazy"
                        />
                      </StoreDownloadLink>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Social Links Row - Mobile Touch Optimized */}
          <motion.div
            variants={itemVariants}
            className="mt-6"
          >
            <div className="flex justify-start gap-3">
              {footerLinks.social.map((item) => (
                <motion.a
                  key={item.name}
                  whileHover={{ y: -2, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-11 h-11 rounded-xl bg-black/5 hover:bg-primary/10 hover:text-primary transition-all duration-300 touch-manipulation"
                  style={{ minHeight: '44px', minWidth: '44px' }}
                >
                  <span className="sr-only">{item.name}</span>
                  {item.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Bottom Bar - Mobile Optimized */}
          <motion.div
            variants={itemVariants}
            className="mt-10 pt-6 border-t border-black/5"
          >
            <div className="space-y-6 lg:space-y-0 lg:flex lg:justify-between lg:items-center">
              {/* Copyright and Legal Links */}
              <div className="text-center lg:text-left space-y-4 lg:space-y-0 lg:flex lg:items-center lg:space-x-6">
                <span className="block lg:inline text-gray-600 text-sm">
                  &copy; {new Date().getFullYear()} TipKeepr. All rights reserved.
                </span>
                <div className="h-4 w-px bg-black/10 hidden lg:block" />
                <nav className="flex justify-center lg:justify-start items-center gap-4 lg:gap-6 text-xs lg:text-sm">
                  <Link href="/legal/privacy" className="text-gray-600 hover:text-primary transition-colors py-2 touch-manipulation">
                    Privacy Policy
                  </Link>
                  <span className="text-gray-300">•</span>
                  <Link href="/legal/terms" className="text-gray-600 hover:text-primary transition-colors py-2 touch-manipulation">
                    Terms of Service
                  </Link>
                  <span className="text-gray-300">•</span>
                  <Link href="/legal/cookie" className="text-gray-600 hover:text-primary transition-colors py-2 touch-manipulation">
                    Cookie Policy
                  </Link>
                </nav>
              </div>
              
              {/* Support Link - Mobile Enhanced */}
              <motion.a
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                href="/contact"
                className="flex items-center justify-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors py-3 px-4 rounded-lg bg-primary/5 hover:bg-primary/10 touch-manipulation mx-auto lg:mx-0"
                style={{ minHeight: '44px' }}
              >
                <Mail className="h-4 w-4" />
                <span>Get Support</span>
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
}
